import { z } from 'zod';
import {
  DRAYAGE_SUBTYPES,
  makeFacilityTypeSchema,
  makeHazmatClassQualifierSchema,
  makeHazmatClassSchema,
  makeHazmatPackingGroupSchema,
  makeStandardAccessorialSchema,
  makeStandardDimensionUnitSchema,
  makeStandardEquipmentTypeSchema,
  makeStandardFreightModeSchema,
  makeStandardLineItemTypeSchema,
  makeStandardReferenceTypeSchema,
  makeStandardShippingUnitSchema,
  makeStandardTemperatureUnitSchema,
  makeStandardTimeRangeTypeSchema,
  makeStandardWeightUnitSchema,
  makeStopTypeSchema,
  STANDARD_MILEAGE_UNITS,
  STANDARD_PAYMENT_TERMS,
  STANDARD_TRANSACTION_PURPOSES,
} from '../enums';
import { STOP_SCHEDULING_TYPES } from '../enums';
import type { StopSchedulingType } from '../enums';
import { makeStandardContactSchema } from './contact';
import { makeStandardSchedulingSchema } from './schedule';

export const makeStandardFreightOrderLocationSchema = () => {
  return z.object({
    locationEntityId: z.string().optional().describe('Vooma Internal ID'),
    locationCode: z.string().optional().describe('ID from external system'),
    name: z.string(),
    addressOne: z.string().optional(),
    addressTwo: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zip: z.string().optional(),
    country: z.string().optional(),
    facilityType: makeFacilityTypeSchema().optional(),
    notes: z.string().optional(),
    externalNotes: z.string().optional(),
    geoCoordinates: z
      .object({
        latitude: z.number(),
        longitude: z.number(),
      })
      .optional(),
    scheduling: makeStandardSchedulingSchema().optional(),
  });
};

const standardFreightOrderLocationSchema =
  makeStandardFreightOrderLocationSchema();
export type StandardFreightOrderLocation = z.infer<
  typeof standardFreightOrderLocationSchema
>;

export const makeStandardCarrierSchema = () => {
  return z.object({
    name: z.string(),
    id: z.string().optional(),
    externalId: z.string().optional(),
    mcNumber: z.string().optional(),
    dotNumber: z.string().optional(),
    driver: makeStandardContactSchema().optional(),
    dispatch: makeStandardContactSchema().optional(),
    truckNumber: z.string().optional(),
    trailerNumber: z.string().optional(),
  });
};
const standardCarrierSchema = makeStandardCarrierSchema();
export type StandardCarrier = z.infer<typeof standardCarrierSchema>;

export const makeStandardWeightSchema = () => {
  return z.object({
    value: z.number(),
    unit: makeStandardWeightUnitSchema().optional(),
  });
};
const standardWeightSchema = makeStandardWeightSchema();
export type StandardWeight = z.infer<typeof standardWeightSchema>;

export const makeStandardMileageSchema = () => {
  return z.object({
    value: z.number(),
    units: z.enum(STANDARD_MILEAGE_UNITS),
  });
};
const standardMileageSchema = makeStandardMileageSchema();
export type StandardMileage = z.infer<typeof standardMileageSchema>;

export const makeStandardReferenceSchema = () => {
  return z.object({
    reference: z.string(),
    referenceType: makeStandardReferenceTypeSchema(),
  });
};
const standardReferenceSchema = makeStandardReferenceSchema();
export type StandardReference = z.infer<typeof standardReferenceSchema>;

const DateTimeInterpretationPolicies = ['DATE', 'DATE_AND_TIME'] as const;
const makeDateTimeInterpretationPolicySchema = () =>
  z.enum(DateTimeInterpretationPolicies);
const dateTimeInterpretationPolicySchema =
  makeDateTimeInterpretationPolicySchema();
export type DateTimeInterpretationPolicy = z.infer<
  typeof dateTimeInterpretationPolicySchema
>;

export const makeStandardDateTimeSchema = () => {
  return z.object({
    dateTime: z
      .string()
      .date()
      .or(z.string().datetime({ local: true, offset: true }))
      .describe('ISO 8601 datetime, or a bare date for date-only stops'),
    timezone: z.string().optional().describe('IANA timezone'),
    interpretationPolicy: makeDateTimeInterpretationPolicySchema(),
  });
};
const standardDateTimeSchema = makeStandardDateTimeSchema();
export type StandardDateTime = z.infer<typeof standardDateTimeSchema>;

export const makeStandardDateTimeRangeSchema = () => {
  return z.object({
    start: makeStandardDateTimeSchema(),
    end: makeStandardDateTimeSchema().optional(),
    type: makeStandardTimeRangeTypeSchema().optional(),
  });
};
const standardDateTimeRangeSchema = makeStandardDateTimeRangeSchema();
export type StandardDateTimeRange = z.infer<typeof standardDateTimeRangeSchema>;

// TODO(mike/farzaan/max): Deduplicate. Use StandardDateTimeRange.
export type VoomaDateTimeRange = {
  start?: VoomaDateTime;
  end?: VoomaDateTime;
  timeZone?: string;
};

// TODO(mike/farzaan/max): Deduplicate. Use StandardDateTime.
export type VoomaDateTime = {
  // NOTE(max): ISO 8601 formatted date time string in UTC timezone.
  dateTime: string;
  interpretationPolicy: DateTimeInterpretationPolicy;
  originalOrderTimeOffset?: string;
};

export type VoomaStopTiming = {
  plannedDateTimeRange?: VoomaDateTimeRange;
};

export const makeStandardLineItemSchema = () => {
  return z.object({
    amount: z.number(),
    description: z.string(),
    type: makeStandardLineItemTypeSchema().optional(),
  });
};
const StandardLineItemSchema = makeStandardLineItemSchema();
export type StandardLineItem = z.infer<typeof StandardLineItemSchema>;

export const makeStandardTemperatureSchema = () => {
  return z.object({
    min: z.number().optional(),
    max: z.number().optional(),
    units: makeStandardTemperatureUnitSchema().optional(),
  });
};
const StandardTemperatureSchema = makeStandardTemperatureSchema();
export type StandardTemperature = z.infer<typeof StandardTemperatureSchema>;

export const makeStandardDimensionSchema = () => {
  return z.object({
    value: z.number(),
    units: makeStandardDimensionUnitSchema(),
  });
};
const StandardDimensionSchema = makeStandardDimensionSchema();
export type StandardDimension = z.infer<typeof StandardDimensionSchema>;

export const makeStandardDimensionsSchema = () => {
  return z.object({
    length: makeStandardDimensionSchema().optional(),
    width: makeStandardDimensionSchema().optional(),
    height: makeStandardDimensionSchema().optional(),
  });
};
const StandardDimensionsSchema = makeStandardDimensionsSchema();
export type StandardDimensions = z.infer<typeof StandardDimensionsSchema>;

export const makeStandardEquipmentSchema = () => {
  return z.object({
    equipmentType: makeStandardEquipmentTypeSchema().optional(),
    equipmentLength: makeStandardDimensionSchema().optional(),
    temperature: makeStandardTemperatureSchema().optional(),
    description: z.string().optional(),
  });
};
const standardEquipmentSchema = makeStandardEquipmentSchema();
export type StandardEquipmentDetails = z.infer<typeof standardEquipmentSchema>;

export const makeStandardEquipmentOptionsSchema = () => {
  return z.union([standardEquipmentSchema, z.array(standardEquipmentSchema)]);
};
const standardEquipmentOptionsSchema = makeStandardEquipmentOptionsSchema();
export type StandardEquipmentOptions = z.infer<
  typeof standardEquipmentOptionsSchema
>;

export const makeHazmatContactSchema = () => {
  return z.object({
    ...makeStandardContactSchema().shape,
    // NOTE(jacob): 443 Contact Inquiry Reference - Additional reference number
    // or description to clarify a contact number. Often a contract or customer
    // number.
    reference: z.string().optional(),
  });
};
const hazmatContactSchema = makeHazmatContactSchema();
export type HazmatContact = z.infer<typeof hazmatContactSchema>;

export const makeHazmatClassificationSchema = () => {
  return z.object({
    hazardClass: makeHazmatClassSchema(),
    qualifier: makeHazmatClassQualifierSchema(),
  });
};
const hazmatClassificationSchema = makeHazmatClassificationSchema();
export type HazmatClassification = z.infer<typeof hazmatClassificationSchema>;

export const makeStandardHazmatDetailsSchema = () => {
  return z.object({
    contact: makeHazmatContactSchema().optional(),
    weight: makeStandardWeightSchema(),
    unNumber: z.string().optional(),
    packingGroup: makeHazmatPackingGroupSchema().optional(),
    classifications: z.array(makeHazmatClassificationSchema()),
  });
};
const standardHazmatDetailsSchema = makeStandardHazmatDetailsSchema();
export type StandardHazmatDetails = z.infer<typeof standardHazmatDetailsSchema>;

export const makeStandardCommodityDetailsSchema = () => {
  return z.object({
    description: z.string().optional(),
    handlingQuantity: z.number().optional(),
    handlingUnits: makeStandardShippingUnitSchema().optional(),
    pieceQuantity: z.number().optional(),
    pieceUnits: makeStandardShippingUnitSchema().optional(),
    freightClass: z.string().optional(),
    nmfcCode: z.string().optional(),
    hazmat: makeStandardHazmatDetailsSchema().optional(),
    weight: makeStandardWeightSchema().optional(),
    grossWeight: makeStandardWeightSchema()
      .optional()
      .describe('Weight including packaging, when stated separately.'),
    itemReference: z.string().optional(),
    dimensions: makeStandardDimensionsSchema().optional(),
    // TODO(max): Handle pick/drop stop ids for multi-pick/stop orders.
  });
};
const standardCommodityDetailsSchema = makeStandardCommodityDetailsSchema();
export type StandardCommodityDetails = z.infer<
  typeof standardCommodityDetailsSchema
>;

export const makeStandardFreightModeDetailsSchema = () => {
  return z.object({
    mode: makeStandardFreightModeSchema(),
    subType: z
      .enum(DRAYAGE_SUBTYPES)
      .optional()
      .describe('Only for DRAYAGE mode.'),
  });
};
const standardFreightModeDetailsSchema = makeStandardFreightModeDetailsSchema();
export type StandardFreightModeDetails = z.infer<
  typeof standardFreightModeDetailsSchema
>;

export const makeStandardFreightOrderStopSchema = () => {
  return z.object({
    id: z.string().optional(),
    externalId: z.string().optional(),
    stopReferences: z.array(makeStandardReferenceSchema()),
    stopType: makeStopTypeSchema(),
    location: makeStandardFreightOrderLocationSchema(),
    dateTimeRanges: z.array(makeStandardDateTimeRangeSchema()).optional(),
    weight: makeStandardWeightSchema().optional(),
    commodities: z.array(makeStandardCommodityDetailsSchema()).optional(),
    accessorials: z.array(makeStandardAccessorialSchema()).optional(),
    contact: makeStandardContactSchema().optional(),
    notes: z
      .string()
      .optional()
      .describe('Internal notes, not shared with carrier.'),
    externalNotes: z
      .string()
      .optional()
      .describe(
        'Shared externally, eg. shown to carrier on rate confirmation.'
      ),
  });
};
const standardFreightOrderStopSchema = makeStandardFreightOrderStopSchema();
export type StandardFreightOrderStop = z.infer<
  typeof standardFreightOrderStopSchema
> & {
  // NOTE(mike): Everything below needs to be cleaned up, so it's excluded from the zod schema.

  // TODO(mike/farzaan/max): Deduplicate. Use dateTimeRanges.
  timing?: VoomaStopTiming;
  schedulingType?: StopSchedulingType;

  // NOTE(martin): when the TMS reported the stop as done — Turvo's per-stop
  // departed (or arrival) timestamp. Absent for TMSes without that signal.
  clearedAt?: string;
  // NOTE(martin): when the truck reached the stop. Differs from clearedAt,
  // which prefers departure, so dwell time doesn't count as lateness.
  arrivedAt?: string;
  // NOTE(martin): the TMS-reported departure only; clearedAt can fall back to
  // arrival when a stop is completed without one.
  departedAt?: string;

  // NOTE(max): `date` and `time` are legacy fields for old templates and
  // unmigrated outflow integrations. `datetime` is the new field
  // that supports date and time ranges.
  // NOTE(max): `date` and `time` are parsed as UTC times which means they
  // must be relocalized to the proper timezone for use in some APIs.
  // `dateTimeRanges` uses moments with timezone information so
  // are pre-localized to the right timezone during conversion.
  // See: https://momentjs.com/timezone/
  date?: moment.Moment;
  time?: moment.Moment;
};

// NOTE(wassim): the authorable subset excludes system-assigned and computed
// fields; it is the shared contract for agent/user order authoring (the
// create_order tool schema and the fromStandardFreightBase serializer input).
// NOTE(wassim): stored orders carry null address components, so the
// authorable subset tolerates them; the serializer coalesces to ''.
export const makeAuthorableFreightOrderLocationSchema = () =>
  makeStandardFreightOrderLocationSchema()
    .omit({
      geoCoordinates: true,
      scheduling: true,
    })
    .extend({
      addressOne: z.string().nullish(),
      addressTwo: z.string().nullish(),
      city: z.string().nullish(),
      state: z.string().nullish(),
      zip: z.string().nullish(),
      country: z.string().nullish(),
    });

export const makeAuthorableFreightOrderStopSchema = () =>
  makeStandardFreightOrderStopSchema()
    .omit({
      commodities: true,
      externalId: true,
      id: true,
      weight: true,
    })
    .extend({
      location: makeAuthorableFreightOrderLocationSchema(),
      schedulingType: z.enum(STOP_SCHEDULING_TYPES).optional(),
      dateTimeRanges: z
        .array(makeStandardDateTimeRangeSchema())
        .max(1)
        .optional()
        .describe('The stop window. Orders store one window per stop.'),
    });
const authorableFreightOrderStopSchema = makeAuthorableFreightOrderStopSchema();
export type AuthorableFreightOrderStop = z.infer<
  typeof authorableFreightOrderStopSchema
>;

export const makeStandardWorkflowMetadataSchema = () => {
  return z.object({
    attributes: z.record(z.string()).optional(),
    tags: z.array(z.string()).optional(),
  });
};
const standardWorkflowMetadataSchema = makeStandardWorkflowMetadataSchema();
export type StandardWorkflowMetadata = z.infer<
  typeof standardWorkflowMetadataSchema
>;

export const makeStandardOriginatorSchema = () => {
  return z.object({
    id: z.string().optional(),
    transmitId: z.string().optional(),
    name: z.string(),
    contacts: z.array(makeStandardContactSchema()).optional(),
  });
};
const standardOriginatorSchema = makeStandardOriginatorSchema();
export type StandardOriginator = z.infer<typeof standardOriginatorSchema>;

export const makeStandardFreightBaseSchema = () => {
  return z.object({
    orderId: z.string(),
    externalId: z.string().optional(),
    orderReferences: z
      .array(makeStandardReferenceSchema())
      .describe('Order-level reference numbers.'),
    transactionPurpose: z.enum(STANDARD_TRANSACTION_PURPOSES).optional(),
    stops: z.array(makeStandardFreightOrderStopSchema()),
    freightMode: makeStandardFreightModeDetailsSchema(),
    equipment: makeStandardEquipmentSchema().optional(),
    accessorials: z.array(makeStandardAccessorialSchema()).optional(),
    notes: z.string().optional(),
    lineItems: z
      .array(makeStandardLineItemSchema())
      .describe('Customer-facing rate/charge lines; empty array when none.'),
    buyRate: z
      .array(makeStandardLineItemSchema())
      .optional()
      .describe('Carrier cost lines, when stated.'),
    customerCharges: z
      .object({ lineItems: z.array(makeStandardLineItemSchema()) })
      .optional(),
    declaredValue: z.number().optional(),
    contact: makeStandardContactSchema()
      .optional()
      .describe('Order-level contact, distinct from any stop contact.'),
    weight: makeStandardWeightSchema()
      .optional()
      .describe('Total order weight.'),
    dimensions: makeStandardDimensionsSchema().optional(),
    linearSize: makeStandardDimensionSchema()
      .optional()
      .describe('Trailer deck length the whole order occupies.'),
    commodities: z.array(makeStandardCommodityDetailsSchema()).optional(),
    mileage: makeStandardMileageSchema().optional(),
    isStackable: z.boolean().optional(),
    paymentTerms: z.enum(STANDARD_PAYMENT_TERMS).optional(),
    workflowMetadata: makeStandardWorkflowMetadataSchema().optional(),
  });
};
const standardFreightBaseSchema = makeStandardFreightBaseSchema();
export type StandardFreightBase = Omit<
  z.infer<typeof standardFreightBaseSchema>,
  'stops'
> & {
  // NOTE(wassim): stops keep the legacy fields excluded from the zod schema.
  stops: StandardFreightOrderStop[];
};

export const makeAuthorableFreightOrderSchema = () =>
  makeStandardFreightBaseSchema()
    .omit({
      customerCharges: true,
      externalId: true,
      orderId: true,
    })
    .extend({
      stops: z
        .array(makeAuthorableFreightOrderStopSchema())
        .min(1)
        .describe('All stops in sequence, pickups first.'),
    });
const authorableFreightOrderSchema = makeAuthorableFreightOrderSchema();
export type AuthorableFreightOrder = z.infer<
  typeof authorableFreightOrderSchema
>;
